import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PmMicroScenarioView } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../auth/keycloak.js";
import { useInstituteLabel } from "./pm-institutes.js";
import { TagChip, tagsOfTask } from "./pm-tags.js";
import { usePmTaskWrites } from "./usePmBoard.js";

/**
 * The meeting list (ADR-055 phase 6): the current phase's tasks marked "In Meeting besprechen", in board order —
 * what the sprint meeting walks through. A row opens the task; "Markierung entfernen" clears the flag once it has been
 * discussed (an idempotent set, so two people clearing it at once is fine).
 */
export function PmMeeting({ session, data, onOpenTask }: { session: AuthSession; data: PmMicroScenarioView; onOpenTask: (taskId: number) => void }) {
  const { t } = useTranslation();
  const instituteLabel = useInstituteLabel();
  const writes = usePmTaskWrites(session);
  const [error, setError] = useState<string | null>(null);
  const order = new Map(data.columns.map((c, i) => [c.id, i]));
  const tasks = data.tasks.filter((x) => x.flaggedForMeeting).sort((a, b) => (order.get(a.columnId) ?? 0) - (order.get(b.columnId) ?? 0));
  const column = (id: number) => data.columns.find((c) => c.id === id)?.title ?? "";
  const stepLabel = (stepId: number | null) => {
    const s = data.steps.find((x) => x.id === stepId);
    return s ? `${s.number} · ${s.title}` : t("pmBoard.kanban.wholeMs");
  };

  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">{t("pmBoard.nav.meeting")}</h1>
        <p className="text-muted-foreground m-0 text-sm">{t("pmBoard.meetingView.intro")}</p>
      </div>
      {error ? (
        <p role="alert" className="text-destructive m-0 text-sm">
          {error}
        </p>
      ) : null}
      {tasks.length === 0 ? <p className="bg-background text-muted-foreground m-0 rounded-xl p-5 text-sm shadow-sm">{t("pmBoard.meetingView.empty")}</p> : null}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {tasks.map((task) => (
          <li key={task.id} className="bg-background flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 shadow-sm">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Button variant="ghost" onPress={() => onOpenTask(task.id)} className="h-auto justify-start p-0 text-left text-sm font-semibold whitespace-normal hover:bg-transparent hover:underline">
                {task.title}
              </Button>
              <span className="text-muted-foreground text-xs">
                {stepLabel(task.stepId)} · {t("pmBoard.meetingView.inColumn", { column: column(task.columnId) })} ·{" "}
                {t("pmBoard.kanban.byline", { institute: instituteLabel(task.createdBy), name: task.createdBy.name })}
              </span>
              {task.tagIds.length > 0 ? (
                <span className="flex flex-wrap gap-1.5">
                  {tagsOfTask(data.tags, task).map((g) => (
                    <TagChip key={g.id} tag={g} />
                  ))}
                </span>
              ) : null}
            </div>
            <Button
              variant="outline"
              size="sm"
              aria-label={t("pmBoard.meetingView.unmarkLabel", { title: task.title })}
              isDisabled={writes.meeting.isPending && writes.meeting.variables?.id === task.id}
              onPress={() => {
                setError(null);
                // Per-call promise, not `mutate` callbacks: clearing two rows quickly must report both (diff panel).
                writes.meeting.mutateAsync({ id: task.id, flagged: false }).catch(() => setError(t("pmBoard.meetingView.unmarkFailed")));
              }}
            >
              {t("pmBoard.meetingView.unmark")}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
